package dev.danko.roblox_craft_ai.shared.model

import kotlinx.serialization.Serializable

/** Roblox limits the model viewer checks against. */
object RobloxMeshLimits {
    const val MAX_TRIANGLES = 20_000
    const val MAX_TEXTURE = 1024
    const val MIN_SIZE_STUDS = 0.5
    const val MAX_SIZE_STUDS = 200.0
    /** Height of the default R15 avatar, for the scale reference. */
    const val R15_HEIGHT_STUDS = 5.2
    val TEXTURE_SIZES = listOf(256, 512, 1024)
    /** Triangle target that keeps the generator's detailed mesh (high-poly, over the MeshPart limit). */
    const val KEEP_DETAIL = 0
    const val MIN_TRIANGLES = 500
    /** Target when a request does not name one: enough for a character. */
    const val DEFAULT_TRIANGLES = 12_000

    fun isValidTarget(triangles: Int) = triangles == KEEP_DETAIL || triangles in MIN_TRIANGLES..MAX_TRIANGLES
}

/** What the server knows about a 3D work's model file; null = could not be measured (no mesh service). */
@Serializable
data class MeshInfo(
    val triangles: Int? = null,
    val vertices: Int? = null,
    val sizeStuds: List<Double>? = null,
    val textureSize: Int? = null,
    /** A normal map carries the detailed model's surface relief. */
    val normalMap: Boolean = false,
    val parts: Int? = null,
    val smallParts: Int? = null,
    val bottomY: Double? = null,
    val centerOffset: Double? = null,
    val fileBytes: Long? = null,
    /** Turn the generator's front was given automatically (0/90/180/270), null when it was left as generated. */
    val orientationYaw: Int? = null,
    /** The user turned the model by hand, so its front is confirmed. */
    val frontSetByUser: Boolean = false,
    val provider: String? = null,
    /** False when this server has no mesh service: edits are not available. */
    val editable: Boolean = false,
    /** The generator's detailed model, kept for a while: high-poly download and triangle budgets in both directions. */
    val sourceUrl: String? = null,
    val sourceTriangles: Int? = null,
    /** The current version with a template R15 skeleton, when bones have been placed. */
    val riggedUrl: String? = null
)

/** One or more fixes for a 3D work; each request makes a new version of the model. */
@Serializable
data class MeshEditRequest(
    val targetTriangles: Int? = null,
    val sizeStuds: Double? = null,
    /** Turn about the vertical axis, a multiple of 90. */
    val rotateDegrees: Int = 0,
    val maxTexture: Int? = null,
    val dropSmallParts: Boolean = false
)

/** Names of the pictures of one object for image-to-3D; the front is the main image. */
object MeshViews {
    const val FRONT = "front"
    const val BACK = "back"
    const val LEFT = "left"
    const val RIGHT = "right"
    val ALL = listOf(FRONT, BACK, LEFT, RIGHT)
}

/** A model sheet (one picture with several views) to cut into separate views. */
@Serializable
data class SplitViewsRequest(val image: String)

/** View name ([MeshViews]) -> PNG data URL; a picture that is not a sheet comes back as the front only. */
@Serializable
data class SplitViewsResponse(val views: Map<String, String>)

/** «Сменить текстуру»: a style in words, or the user's own style picture (data URL), for a finished model. */
@Serializable
data class RetextureRequest(val style: String = "", val styleImage: String? = null)

/** A change to a finished animation in words ("wider arm swings", "return to the start faster"). */
@Serializable
data class AnimationEditRequest(val instruction: String)

/** What an imported model is for: sets its size in studs (a character 5 studs tall, a weapon 4 long). */
@Serializable
enum class ModelImportKind { CHARACTER, WEAPON, OTHER }

/** A picture of a work the browser rendered (PNG data URL). */
@Serializable
data class ModelPreviewRequest(val dataUrl: String)
